import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HiX,
  HiChatAlt2,
  HiUser,
  HiCreditCard,
  HiQuestionMarkCircle,
  HiDocumentText,
  HiShieldCheck,
  HiAnnotation
} from 'react-icons/hi';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

// Import environment variable
const enableStripe = import.meta.env.VITE_ENABLE_STRIPE === 'true';

interface MobileMenuProps {
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ onClose }) => {
    const { userProfile, logout, authVersion } = useAuth();
    const navigate = useNavigate();
    
    // Compute login state directly without useMemo to ensure fresh evaluation
    const isLoggedIn = Boolean(userProfile && !userProfile.isAnonymous);
        
    // Debug log when component mounts
    useEffect(() => {
        logger.debug('MobileMenu', `Component mounted with authVersion=${authVersion}, isLoggedIn=${isLoggedIn}`);
        return () => {
          logger.debug('MobileMenu', 'Component unmounting');
        };
      }, [authVersion, isLoggedIn]);    

    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogout = async () => {
        try {
          if (isLoggingOut) return;
          
          logger.info('MobileMenu', 'Logout button clicked');
          setIsLoggingOut(true);
          logger.info('MobileMenu', 'onClose');
          
          // Close menu first
          onClose();
          
          // Small delay 
          await new Promise(resolve => setTimeout(resolve, 50));
          logger.info('MobileMenu', 'navigate /');
          
          // Navigate before logout
          navigate('/', { replace: true });
          
          // Another small delay
          await new Promise(resolve => setTimeout(resolve, 50));
          logger.info('MobileMenu', 'logout');

          // Perform logout
          await logout();

      } catch (error) {
          logger.error('MobileMenu', 'Logout failed', error);
          setIsLoggingOut(false);
        }
      };
  // Handle clicking the background to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close only if clicking the backdrop (not the menu itself)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={handleBackdropClick}>
      <div className="absolute right-0 top-0 bottom-0 w-[80%] max-w-sm bg-white shadow-xl flex flex-col h-full">
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-gray-200">
          <h2 className="font-medium text-lg">Menu</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close menu">
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {/* Auth State Section */}
          <div className="mb-6 pb-4 border-b border-gray-200">
            {isLoggedIn ? (
              <div className="flex items-center p-2">
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                  {userProfile?.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="ml-3">
                  <p className="font-medium">{userProfile?.name || userProfile?.email}</p>
                  <p className="text-sm text-gray-500">
                    {userProfile?.isSubscribed ? 'Premium Plan' : 'Free Plan'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link to="/login" className="w-full py-2 px-3 bg-blue-600 text-white rounded-lg text-center" onClick={onClose}>
                  Log In
                </Link>
                <Link to="/signup" className="w-full py-2 px-3 border border-gray-300 text-gray-700 rounded-lg text-center" onClick={onClose}>
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Main Navigation */}
          <nav className="space-y-1 mb-6 pb-4 border-b border-gray-200">
            <Link
              to="/chat"
              className="flex items-center p-3 rounded-lg hover:bg-gray-100"
              onClick={onClose}
            >
                <HiChatAlt2 className="w-5 h-5 text-gray-500 mr-3" />
              <span>Chat</span>
            </Link>

            {enableStripe && (
              <Link
                to="/pricing"
                className="flex items-center p-3 rounded-lg hover:bg-gray-100"
                onClick={onClose}
              >
                <HiCreditCard className="w-5 h-5 text-gray-500 mr-3" />
                <span>Pricing</span>
              </Link>
            )}
          </nav>

          {/* Resources */}
          <div className="space-y-1 mb-6 pb-4 border-b border-gray-200">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Resources</h3>

            <Link
              to="/faq"
              className="flex items-center p-3 rounded-lg hover:bg-gray-100"
              onClick={onClose}
            >
              <HiQuestionMarkCircle className="w-5 h-5 text-gray-500 mr-3" />
              <span>FAQ</span>
            </Link>
          </div>

          {/* Legal */}
          <div className="space-y-1 mb-6">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Legal</h3>

            <Link
              to="/terms"
              className="flex items-center p-3 rounded-lg hover:bg-gray-100"
              onClick={onClose}
            >
              <HiDocumentText className="w-5 h-5 text-gray-500 mr-3" />
              <span>Terms of Service</span>
            </Link>

            <Link
              to="/privacy"
              className="flex items-center p-3 rounded-lg hover:bg-gray-100"
              onClick={onClose}
            >
              <HiShieldCheck className="w-5 h-5 text-gray-500 mr-3" />
              <span>Privacy Policy</span>
            </Link>
          </div>

          {/* Logout */}
          {isLoggedIn && (
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={`flex items-center w-full text-left p-3 rounded-lg hover:bg-gray-100 text-red-600 ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-9V4m-6 8h.01" />
              </svg>
              <span>{isLoggingOut ? 'Signing Out...' : 'Sign Out'}</span>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 text-xs text-gray-500">
          Version 1.0.0
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;